import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { GoogleReviewImportRow } from '../google-takeout';
import { SiteService } from './site.service';

/**
 * Website → Google reviews (#173). Thin wrapper over /api/google-reviews —
 * a table, not account/website content, so it does not ride account$.
 */

export type GoogleReviewStatus = 'pending' | 'approved' | 'suppressed';
export type GoogleReviewAction = 'approve' | 'unapprove' | 'suppress' | 'unsuppress';

export interface GoogleReview {
  id: number;
  googleReviewId: string;
  locationId: string;
  locationName: string | null;
  reviewerName: string | null;
  starRating: number | null;
  comment: string | null;
  replyComment: string | null;
  /** ISO 8601, UTC. */
  createTime: string | null;
  updateTime: string | null;
  status: GoogleReviewStatus;
  /** 'edited' when an approved review changed on Google and came back for re-checking. */
  requeueReason: string | null;
  reviewedBy: number | null;
  reviewedAt: string | null;
  arrangerUserIds: number[];
  source: 'takeout' | 'api';
  /** Set when the latest import of its listing no longer had it. */
  removedFromGoogleAt: string | null;
  lastSeenAt: string;
}

export interface GoogleReviewImportSummary {
  received: number;
  added: number;
  changed: number;
  requeued: number;
  unchanged: number;
  removed: number;
  restored: number;
  locations: number;
}

@Injectable({
  providedIn: 'root',
})
export class GoogleReviewService {
  constructor(private http: HttpClient, private siteService: SiteService) {}

  list(): Observable<GoogleReview[]> {
    return this.logged('list', this.http.get<{ data: GoogleReview[] }>(this.url())).pipe(map((r) => r.data));
  }

  import(
    reviews: GoogleReviewImportRow[],
    locationIds: string[]
  ): Observable<GoogleReviewImportSummary> {
    return this.logged(
      'import',
      this.http.post<{ data: GoogleReviewImportSummary }>(this.url('/import'), {
        source: 'takeout',
        reviews,
        locationIds,
      })
    ).pipe(map((r) => r.data));
  }

  moderate(ids: number[], action: GoogleReviewAction): Observable<number> {
    return this.logged(
      'moderate',
      this.http.patch<{ data: { updated: number } }>(this.url('/moderate'), { ids, action })
    ).pipe(map((r) => r.data.updated));
  }

  /** Current staff a review can be attributed to (id + name only). */
  arrangers(): Observable<{ id: number; firstName: string; lastName: string }[]> {
    return this.logged(
      'arrangers',
      this.http.get<{ data: { id: number; firstName: string; lastName: string }[] }>(this.url('/arrangers'))
    ).pipe(map((r) => r.data));
  }

  /** Google listings that have approved reviews (a Reviews block's listing scope). */
  listings(): Observable<{ id: string; name: string; approved: number }[]> {
    return this.logged(
      'listings',
      this.http.get<{ data: { id: string; name: string; approved: number }[] }>(this.url('/listings'))
    ).pipe(map((r) => r.data));
  }

  setArrangers(id: number, arrangerUserIds: number[]): Observable<GoogleReview> {
    return this.logged(
      'setArrangers',
      this.http.patch<{ data: GoogleReview }>(this.url(`/${id}/arrangers`), { arrangerUserIds })
    ).pipe(map((r) => r.data));
  }

  private url(suffix = ''): string {
    return environment.api.host + environment.api.paths.api.googleReviews.base + suffix;
  }

  private logged<T>(name: string, request: Observable<T>): Observable<T> {
    const key = `google-review.service.ts->${name}`;
    this.siteService.addSubscriptionLog(this, key);
    return request.pipe(finalize(() => this.siteService.setSubscriptionLogFinalised(key)));
  }
}
